import { download, query } from '../../api/client'
import { useQuery } from '../../api/hooks'
import type { CashFlowLine, CashFlowStatement } from '../../api/types'
import { useAuth } from '../../auth/AuthContext'
import { P } from '../../auth/permissions'
import { Empty, ErrorBanner, Loading, Money, Panel, formatDate, money, useDateRange } from '../../components/ui'

/** Direct-method movement in every ledger account marked as a cash equivalent. */
export function CashFlowPage() {
  const { can } = useAuth()
  const { from, to, setFrom, setTo } = useDateRange()
  const report = useQuery<CashFlowStatement>(`/reports/cash-flow${query({ from, to })}`, [from, to])

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label>To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="spacer" />
        {can(P.reports.export) && (
          <button
            onClick={() =>
              void download(
                `/reports/cash-flow/excel${query({ from, to })}`,
                `cash-flow-${from}-to-${to}.xlsx`,
              )
            }
          >
            Export to Excel
          </button>
        )}
      </div>

      <ErrorBanner error={report.error} />
      {report.loading && <Loading what="Tracing cash movements" />}

      {report.data && (
        <>
          {!report.data.reconciles && (
            <div className="banner error">
              <strong>The cash-flow statement does not reconcile.</strong> Its calculated closing
              cash differs from the ledger by {money(report.data.difference)}.
            </div>
          )}

          <Panel
            title={
              <h2>
                Cash-flow statement{' '}
                <span className="muted" style={{ fontWeight: 400 }}>
                  · {formatDate(report.data.from)} to {formatDate(report.data.to)}
                </span>
              </h2>
            }
            flush
          >
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 100 }}>Code</th>
                    <th>Activity</th>
                    <th style={{ width: 140 }} className="num">Inflows</th>
                    <th style={{ width: 140 }} className="num">Outflows</th>
                    <th style={{ width: 140 }} className="num">Net cash flow</th>
                  </tr>
                </thead>
                <tbody>
                  <Section heading="Operating activities" lines={report.data.operating} total={report.data.netOperating} />
                  <Section heading="Investing activities" lines={report.data.investing} total={report.data.netInvesting} />
                  <Section heading="Financing activities" lines={report.data.financing} total={report.data.netFinancing} />
                </tbody>
                <tfoot>
                  <Summary label="Net change in cash and cash equivalents" value={report.data.netChange} />
                  <Summary label="Cash and cash equivalents at start" value={report.data.openingCash} />
                  <Summary label="Cash and cash equivalents at end" value={report.data.closingCash} strong />
                </tfoot>
              </table>
            </div>
          </Panel>

          <p className="muted" style={{ fontSize: 12 }}>
            Direct method. Internal transfers between banks and safes net to zero. Client trust
            accounts are included in cash and cash equivalents; movements in the matching client
            liability accounts appear under operating activities and remain subject to the client
            funds control report.
          </p>
        </>
      )}
    </>
  )
}

function Section({ heading, lines, total }: { heading: string; lines: CashFlowLine[]; total: number }) {
  return (
    <>
      <tr>
        <td colSpan={5} className="strong" style={{ paddingTop: 14 }}>{heading.toUpperCase()}</td>
      </tr>
      {lines.length === 0 && (
        <tr><td /><td colSpan={4}><Empty>Nothing in this period.</Empty></td></tr>
      )}
      {lines.map((line) => (
        <tr key={`${line.activity}-${line.code}`}>
          <td className="code">{line.code}</td>
          <td>{line.nameEn}<span className="muted"> · {line.nameAr}</span></td>
          <td className="num"><Money value={line.inflows} blankZero /></td>
          <td className="num"><Money value={line.outflows} blankZero /></td>
          <td className="num"><Money value={line.net} blankZero /></td>
        </tr>
      ))}
      <tr className="subtotal">
        <td /><td>NET CASH FROM {heading.toUpperCase()}</td><td /><td />
        <td className="num"><Money value={total} /></td>
      </tr>
    </>
  )
}

function Summary({ label, value, strong = false }: { label: string; value: number; strong?: boolean }) {
  return (
    <tr className={strong ? 'subtotal' : undefined}>
      <td /><td colSpan={3} className={strong ? 'strong' : undefined}>{label}</td>
      <td className="num"><Money value={value} /></td>
    </tr>
  )
}
